import { cn } from "@/lib/utils";
import { SCORE_EFFORT } from "@/lib/domain-constants";
import type { AnalysisRow, AnalysisRowStatus } from "@/types/workadvisory";

const STATUS: Record<AnalysisRowStatus, { label: string; tone: string }> = {
  CUMPLE: {
    label: "Cumple",
    tone: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  },
  CUMPLE_CON_OBSERVACION: {
    label: "Cumple con obs.",
    tone: "bg-emerald-50/70 text-emerald-700 dark:bg-emerald-950/25 dark:text-emerald-300",
  },
  SE_COMPROMETE: {
    label: "Se compromete",
    tone: "bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300",
  },
  REVISAR: {
    label: "Revisar",
    tone: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  },
  NO_CUMPLE: {
    label: "No cumple",
    tone: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  },
  NO_APLICA: { label: "No aplica", tone: "bg-muted text-muted-foreground" },
};

/**
 * Lo que piden las bases contra lo que tiene la empresa. Cada fila lleva la
 * página del pliego de donde salió el requisito: sin la cita, el cliente no
 * puede verificar nada y el informe se vuelve una opinión.
 */
export function WorkRequirements({ rows }: { rows: AnalysisRow[] }) {
  const hard = rows.filter((row) => row.categoria === "DURO");
  const points = rows.filter((row) => row.categoria === "PUNTAJE");

  const table = (title: string, list: AnalysisRow[]) =>
    list.length === 0 ? null : (
      <div>
        <h4 className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          {title}
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-xs">
            <tbody>
              {list.map((row, index) => (
                <tr key={index} className="border-t align-top">
                  <td className="w-52 py-2.5 pr-3">
                    <div className="font-semibold">{row.requisito}</div>
                    {row.puntos && (
                      <div
                        className={cn(
                          "text-[10px]",
                          SCORE_EFFORT[row.puntos.esfuerzo]?.tone,
                        )}
                      >
                        {row.puntos.acreditados}/{row.puntos.delFactor} pts ·{" "}
                        {SCORE_EFFORT[row.puntos.esfuerzo]?.label}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-muted-foreground">
                    {row.piden}
                  </td>
                  <td className="py-2.5 pr-3">
                    {row.tiene}
                    {row.porque && (
                      <div className="mt-0.5 text-[11px] italic text-muted-foreground">
                        {row.porque}
                      </div>
                    )}
                  </td>
                  <td className="w-28 py-2.5 pr-3">
                    <span
                      className={cn(
                        "whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase",
                        STATUS[row.estado]?.tone,
                      )}
                    >
                      {STATUS[row.estado]?.label ?? row.estado}
                    </span>
                  </td>
                  <td className="w-16 py-2.5 text-right text-[11px] text-muted-foreground">
                    {row.pagina ? `pág. ${row.pagina}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );

  return (
    <div className="space-y-4 rounded-lg bg-muted/40 p-3">
      <p className="text-[11px] text-muted-foreground">
        Cada fila indica la página de las bases de donde salió el requisito.
      </p>
      {table("Requisitos de calificación", hard)}
      {table("Factores de puntaje", points)}
    </div>
  );
}
